import { API, post } from 'utils/api';
import { logError } from 'utils/log';

import { isLesserThanOrEqualTo, isOneOf } from './validators';
import { __t, applyTranslations } from './language';

import type {
  HelperInstagramImageSize,
  ImageCandidate,
  ImageFile,
  ImageStandard,
  RealmOutfitImageProps
} from 'types';

applyTranslations(require('./i18n/image-util.i18n.json'));

// 50MB
export const IMAGE_MAXIMUM_SIZE = 1024 * 1024 * 50;

export const IMAGE_ALLOWED_MIMETYPES = [
  'image/png',
  'image/jpeg',
  'image/pjpeg',
  'image/gif'
];

export const DEFAULT_PLACEHOLDER_IMAGE = '/static/images/image-404-puzzle-placeholder.png';
export const LOADING_PLACEHOLDER_IMAGE = '/static/images/influencer-tile-image-grid-item-loading.gif';

export function validateImage(file: ImageFile): string | undefined | null {
  const validators = [
    isLesserThanOrEqualTo(
      file.size,
      IMAGE_MAXIMUM_SIZE,
      __t('image_util.size_exceeded', {
        maxSize: IMAGE_MAXIMUM_SIZE / 1024 / 1024
      })
    ),
    isOneOf(file.type, IMAGE_ALLOWED_MIMETYPES, __t('image_util.invalid_type'))
  ];

  const invalid = validators.find(v => !v.valid);
  return invalid ? invalid.message : null;
}

export const getPreparedPreloadImage = (
  item: ImageCandidate,
  assetUrl: string
): Promise<any> => new Promise((resolve) => {
  const image = new Image();

  image.onload = () => resolve({
    ...item,
    assetUrl
  });

  image.onerror = () => resolve({
    ...item,
    assetUrl: ''
  });

  image.src = assetUrl;
});

export const getImageUrl = (
  url: string | undefined | null,
  code: string,
  type: string,
  size: HelperInstagramImageSize,
  imageUrl?: string | null
): string => {
  if (type === 'youtube') {
    return `https://i.ytimg.com/vi/${code}/mqdefault.jpg`;
  }

  if (type === 'tiktok') {
    return imageUrl || '';
  }

  return imageUrl || '';
};

export const buildImage = (item: ImageCandidate, size: HelperInstagramImageSize = 'm'): ImageStandard => {
  const code = String(item?.code || item?.id || '');
  const type = item.type || 'instagram';
  const url = item.url || '';
  const imageUrl = item.assetUrl || item.imageUrl || null;

  return {
    assetUrl: getImageUrl(url, code, type, size, imageUrl),
    code,
    embedUrl: type === 'youtube' ? `https://www.youtube-nocookie.com/embed/${code}` : null,
    type,
    url
  };
};

export const addPlaceholderImages = (
  images: Array<ImageStandard>,
  numPostsToShow: number,
  isLoading: boolean = false
): Array<ImageStandard> => {
  const updatedImages = [...images];
  const placeholderCount = numPostsToShow - updatedImages.length;

  for (let i = 0; i < placeholderCount; i++) {
    updatedImages.push({
      code: `placeholder-image-${i}`,
      type: isLoading ? 'loading' : 'placeholder',
      assetUrl: '',
      url: ''
    });
  }

  return updatedImages.slice(0, numPostsToShow);
};

export const uploadImage = async (image): Promise<string> => {
  const url = API.END_POINTS.API.IMAGES;
  const data = new FormData();

  data.append('file', image);

  try {
    const response = await post(url, { data });
    return response.data.url;
  } catch (error) {
    logError(error);
    throw error;
  }
};

export const getImageDimension = async (file: File): Promise<RealmOutfitImageProps> => (
  new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (event: ProgressEvent<FileReader>) => {
      const image = new Image();
      image.src = String(event.target?.result);

      image.onload = function handleLoad() {
        // @ts-ignore FileReader
        const height = this.height;
        // @ts-ignore FileReader
        const width = this.width;

        resolve({
          height,
          width,
          ratio: width / height,
          size: file.size
        });
      };

      image.onerror = () => resolve({
        height: 0.1,
        width: 0.1,
        ratio: 0,
        size: 0
      });
    };

    reader.readAsDataURL(file);
  })
);

